import get from 'lodash/get';
import { ExportType, ExportConfig } from '../../../../@types';
import { Header } from '../../../../hooks/exportDataHook';
import { parseDataValue, formatValue } from '../../../../utils/dataUtils';

/**
 * TODO: this interface should be in the types library
 *       due to it being part of the API definition
 */
export interface ExportInputFormat extends Record<string, unknown> {
  groupedAggregates: {
    keys: Array<string>;
    sum: Record<string, number>;
    distinctCount: Record<string, number>;
    min: Record<string, number>;
    max: Record<string, number>;
    average: Record<string, number>;
    stddevSample: Record<string, number>;
    stddevPopulation: Record<string, number>;
    varianceSample: Record<string, number>;
    variancePopulation: Record<string, number>;
  };
}

export const convertExportType = (
  exportType?: ExportType,
): Pick<Header, 'type' | 'format'> => {
  switch (exportType) {
    case 'integer':
      return { type: 'number', format: 'int' };
    case 'decimal':
      return { type: 'number', format: 'float' };
    case 'date':
    case 'time':
      return { type: exportType, format: 'datetime' };
    case 'datetime':
      return { type: 'date', format: 'datetime' };
    case 'string':
    default:
      return { type: 'string' };
  }
};

/**
 * Parses the export headers based on the configs
 * @param config
 * @returns
 */
export const parseHeaders = (config: ExportConfig): Array<Header> =>
  config.columns.map((column) => ({
    data: column.label,
    ...convertExportType(column.outputType),
  }));

/**
 * Parses data for exporting
 * @param input
 * @param config
 * @returns
 */
export const parseExportData = (
  input: Array<Record<string, unknown>>,
  config: ExportConfig,
): Array<Record<string, string | number>> => {
  const parseEntry = (entry: Record<string, unknown>) => {
    const obj: Record<string, string | number> = {};
    config.columns.forEach((c) => {
      if (c.ignoreIfNull) {
        const referenceKeyValue = parseDataValue(
          entry,
          c.ignoreIfNull.key,
          c.ignoreIfNull.dataType || 'string',
        );
        if (referenceKeyValue === 'null') {
          return;
        }
      }

      const parsedValue = parseDataValue(entry, c.key, c.dataType || 'string');

      /**
       * Apply dynamic formatting based on a type field in the same row.
       * This should probably be moved to formatValue, but it'll take a huge refactoring
       * as we don't have access to the entire row inside this function by now.
       *
       * This was implemented for handling aggregated fields on FacilitiesSurveyHistoryPage.
       */
      let { formatter } = c;
      if (formatter?.type === 'dynamic') {
        const formatterKey = formatter?.key;

        if (formatterKey) {
          const formatType = get(entry, formatterKey, '') as string;

          switch (formatType.toLowerCase()) {
            case 'monetary':
              formatter = { type: 'currency', hideCurrency: true };
              break;
            default:
              formatter = undefined;
              break;
          }
        }
      }

      obj[c.label] = formatValue(parsedValue, formatter, c.mapping);
    });
    return obj;
  };

  return input.map((entry) => parseEntry(entry));
};
